import axios from 'axios';
import { getBaseUrl } from '../config/api';

// Get API key from environment variables
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

export interface Coordinates {
  lat: number;
  lng: number;
}

interface GeocodingResponse {
  results: Array<{
    geometry: {
      location: { lat: number; lng: number };
    };
  }>;
  status: string;
}

/**
 * Calls the backend geocoding API (which uses server-side Google Maps API key)
 * Falls back to frontend API if backend fails
 */
async function realGeocode(
  address: string,
  city: string,
  state: string,
  zipCode: string
): Promise<Coordinates | null> {
  try {
    // First, try backend geocoding (more secure, uses server API key)
    try {
      // getBaseUrl() returns URL with /api suffix, so we append /location/geocode
      const backendUrl = getBaseUrl();
      const response = await axios.get(`${backendUrl}/location/geocode`, {
        params: { address, city, state, zipCode },
        timeout: 10000,
      });

      if (response.data?.success && response.data?.data) {
        const { latitude, longitude } = response.data.data;
        console.log('✅ Backend geocoding successful:', { latitude, longitude });
        return { lat: latitude, lng: longitude };
      }
    } catch (backendError) {
      console.warn('⚠️  Backend geocoding failed, trying frontend API:', backendError);
      // Fall through to frontend geocoding
    }

    // Fallback to frontend geocoding if backend fails and API key is available
    if (API_KEY) {
      const formattedAddress = `${address}, ${city}, ${state} ${zipCode}`;
      const response = await axios.get<GeocodingResponse>(
        'https://maps.googleapis.com/maps/api/geocode/json',
        {
          params: { address: formattedAddress, key: API_KEY },
          timeout: 10000,
        }
      );

      if (response.data.status === 'OK' && response.data.results.length > 0) {
        const loc = response.data.results[0].geometry.location;
        console.log('✅ Frontend geocoding successful:', { lat: loc.lat, lng: loc.lng });
        return { lat: loc.lat, lng: loc.lng };
      }
      console.error('❌ Frontend geocoding failed:', response.data.status);
    }

    // If both fail, return null (don't use mock coordinates)
    console.error('❌ All geocoding methods failed - coordinates will not be set');
    return null;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error('❌ Geocoding API error:', err.response?.data || err.message);
    } else {
      console.error('❌ Unexpected geocoding error:', err);
    }
    return null;
  }
}

/**
 * Public service exposing geocoding methods.
 * Always uses real geocoding (backend first, then frontend API if available).
 * Never uses mock coordinates - returns null if all geocoding methods fail.
 */
export const geocodingService = {
  geocodeAddress: realGeocode,

  /**
   * Formats coordinates into a MongoDB GeoJSON Point.
   */
  formatCoordinatesForMongoDB(
    coords: Coordinates | null
  ): { type: 'Point'; coordinates: [number, number] } | null {
    if (!coords) return null;
    return { type: 'Point', coordinates: [coords.lng, coords.lat] };
  },
};

export default geocodingService;
